import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState, useCallback } from "react";
import { Col, Divider, Empty, Input, Modal, Popconfirm, Row, Skeleton, Table, Tag, Tooltip } from "antd";
import { useI18n, useListTcfVendor } from "../../../contexts";
import { jsxJoin } from "@devowl-wp/react-utils";
import { useTableBulkAction } from "../../../hooks";
import { ListMultilingualLanguages } from "../../common";
import { ListTcfNetworkSelector } from ".";
const { Column } = Table;
const ListTcfVendor = ()=>{
    const { __, _i } = useI18n();
    const store = useListTcfVendor();
    const { busy, vendorCount, view, rows } = store;
    const [filters, setFilters] = useState({
        vendor: ""
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [doBulkAction, progress] = useTableBulkAction();
    const [isAdNetworksModalOpen, setIsAdNetworksModalOpen] = useState(()=>view === "vendors" ? !!store.defaultCreateAdNetworkIdentifier : false);
    const adNetworksWithAllVendorIds = useMemo(()=>store.adNetworks.map((network)=>({
                network,
                vendorIds: network.lists.map((param)=>{
                    let { vendorIds } = param;
                    return vendorIds;
                }).flat()
            })), [
        store.adNetworks
    ]);
    // We do currently not support asynchronous pagination, so we expect all vendor / vendor configurations
    // for the table. This also allows us to do filtering and sorting directly within this component.
    const useRows = useMemo(()=>[
            ...rows
        ]// We need to manually sort it again cause the `vendor-list.json#vendors` property uses numeric keys and
        // JavaScript does automatically sort it.
        .sort((param, param1)=>{
            let { vendor: vendorA } = param;
            let { vendor: vendorB } = param1;
            if (!vendorA || !vendorB) {
                // Abanonded vendors should always be on top
                return -1;
            }
            const { name: x } = vendorA;
            const { name: y } = vendorB;
            return x < y ? -1 : x > y ? 1 : 0;
        }).filter((param)=>{
            let { vendor } = param;
            const { vendor: searchVendor } = filters;
            if (vendor && searchVendor) {
                const useTerm = searchVendor.toLowerCase();
                return vendor.id.toString() === useTerm || vendor.name.toLowerCase().indexOf(useTerm) > -1;
            }
            // Always show abandoned vendors
            return true;
        }), [
        rows,
        rows.length,
        filters
    ]);
    const pickRowsByRowKeys = useCallback((keys)=>useRows.filter((param)=>{
            let { configuration, vendor } = param;
            return keys.indexOf(typeof configuration === "boolean" ? vendor.id : configuration.id) > -1 && configuration !== true;
        }), [
        useRows
    ]);
    const addText = __("Create TCF vendor configuration");
    return progress ? progress : vendorCount || view === "vendors" ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                justify: "end",
                gutter: 10,
                children: [
                    view === "vendors" && /*#__PURE__*/ _jsxs(Col, {
                        children: [
                            /*#__PURE__*/ _jsx(Modal, {
                                open: isAdNetworksModalOpen,
                                title: __("Add vendors from ad networks"),
                                onCancel: ()=>setIsAdNetworksModalOpen(false),
                                footer: null,
                                width: 800,
                                children: /*#__PURE__*/ _jsx(ListTcfNetworkSelector, {
                                    onCreate: async (param)=>{
                                        let { list: { vendorIds } } = param;
                                        setIsAdNetworksModalOpen(false);
                                        const picked = pickRowsByRowKeys(vendorIds).map((param)=>{
                                            let { vendor } = param;
                                            return vendor;
                                        });
                                        const [handleItemDone, signal, bulkDone] = doBulkAction({
                                            title: __("Creating vendors..."),
                                            total: picked.length
                                        });
                                        await store.onBulkCreate(picked, handleItemDone, signal, bulkDone);
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                onClick: ()=>setIsAdNetworksModalOpen(true),
                                className: "button right",
                                style: {
                                    marginBottom: 10
                                },
                                children: _i(__("Add vendors from ad networks (e.g. {{img/}} Google Adsense)"), {
                                    img: /*#__PURE__*/ _jsx("img", {
                                        style: {
                                            height: "1em"
                                        },
                                        src: "https://assets.devowl.io/in-app/wp-real-cookie-banner/logos/google-a-dsense.svg"
                                    })
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        style: {
                            width: 400
                        },
                        children: /*#__PURE__*/ _jsx(Input.Search, {
                            autoFocus: true,
                            style: {
                                maxWidth: 400
                            },
                            placeholder: __("Search vendor by name or ID..."),
                            onChange: (e)=>setFilters((filters)=>({
                                        ...filters,
                                        vendor: e.target.value
                                    }))
                        })
                    }),
                    view === "vendor-configurations" && /*#__PURE__*/ _jsx(Col, {
                        children: /*#__PURE__*/ _jsx("a", {
                            onClick: store.onSwitchToVendorView,
                            className: "button button-primary right",
                            style: {
                                marginBottom: 10
                            },
                            children: addText
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    textAlign: "right",
                    marginBottom: 15
                }
            }),
            /*#__PURE__*/ _jsxs(Table, {
                pagination: {
                    pageSize: 50,
                    showTotal: (total, range)=>`${range[0]}-${range[1]} / ${total}`,
                    showSizeChanger: false
                },
                locale: {
                    emptyText: /*#__PURE__*/ _jsx(Empty, {
                        description: __("No data")
                    })
                },
                loading: busy,
                dataSource: useRows,
                rowKey: (param)=>{
                    let { vendor, configuration } = param;
                    return typeof configuration === "boolean" ? vendor.id : configuration.id;
                },
                size: "small",
                bordered: true,
                rowSelection: {
                    type: "checkbox",
                    getCheckboxProps: (param)=>{
                        let { configuration } = param;
                        return {
                            // Disable already created vendors
                            disabled: view === "vendors" && configuration === true
                        };
                    },
                    selectedRowKeys,
                    onChange: (newSelectedRowKeys, _, param)=>{
                        let { type } = param;
                        setSelectedRowKeys(type === "all" ? selectedRowKeys.length > 0 ? [] : useRows.map((param)=>{
                            let { configuration, vendor } = param;
                            return typeof configuration === "boolean" ? configuration ? 0 : vendor.id : configuration.id;
                        }).filter(Boolean) : newSelectedRowKeys);
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx(Column, {
                        title: jsxJoin([
                            ...selectedRowKeys.length > 0 ? [
                                /*#__PURE__*/ _jsx("span", {
                                    children: __("%d selected", selectedRowKeys.length)
                                }, "bulk"),
                                view === "vendors" && /*#__PURE__*/ _jsx(Popconfirm, {
                                    title: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            __("Please note that selecting more vendors than you actually use may lead to ineffective consent. Therefore, only activate the vendors that you actually actively work with!"),
                                            /*#__PURE__*/ _jsx("br", {}),
                                            /*#__PURE__*/ _jsx("br", {}),
                                            __("After activating the TCF vendors, I will check the information for each activated TCF vendor in the TCF vendor configuration myself and correct any information that does not match my use case.")
                                        ]
                                    }),
                                    overlayInnerStyle: {
                                        maxWidth: 300
                                    },
                                    placement: "bottomLeft",
                                    onConfirm: ()=>{
                                        const [handleItemDone, signal, bulkDone] = doBulkAction({
                                            title: __("Creating vendors..."),
                                            total: selectedRowKeys.length
                                        });
                                        store.onBulkCreate(pickRowsByRowKeys(selectedRowKeys).map((param)=>{
                                            let { vendor } = param;
                                            return vendor;
                                        }), handleItemDone, signal, bulkDone);
                                        setSelectedRowKeys([]);
                                    },
                                    okText: __("Create"),
                                    cancelText: __("Cancel"),
                                    children: /*#__PURE__*/ _jsx("a", {
                                        children: __("Create all selected vendors")
                                    })
                                }, "bulk-create"),
                                view === "vendor-configurations" && /*#__PURE__*/ _jsx(Popconfirm, {
                                    title: __("Are you sure you want to delete this vendors?"),
                                    placement: "bottomLeft",
                                    onConfirm: ()=>{
                                        const [handleItemDone, signal, bulkDone] = doBulkAction({
                                            title: __("Deleting vendors..."),
                                            total: selectedRowKeys.length
                                        });
                                        store.onBulkDelete(pickRowsByRowKeys(selectedRowKeys).map((param)=>{
                                            let { configuration } = param;
                                            return configuration;
                                        }), handleItemDone, signal, bulkDone);
                                        setSelectedRowKeys([]);
                                    },
                                    okText: __("Delete all"),
                                    cancelText: __("Cancel"),
                                    children: /*#__PURE__*/ _jsx("a", {
                                        children: __("Delete all")
                                    })
                                }, "bulk-delete")
                            ] : [],
                            /*#__PURE__*/ _jsx("span", {
                                children: __("Vendor")
                            }, "title")
                        ], /*#__PURE__*/ _jsx(Divider, {
                            type: "vertical"
                        })),
                        dataIndex: "vendor",
                        render: (value, _)=>{
                            const { vendor, configuration } = _;
                            return /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    vendor ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            vendor.name,
                                            " ",
                                            /*#__PURE__*/ _jsxs("span", {
                                                style: {
                                                    opacity: 0.5
                                                },
                                                children: [
                                                    "#",
                                                    vendor.id
                                                ]
                                            }),
                                            " "
                                        ]
                                    }) : typeof configuration !== "boolean" && /*#__PURE__*/ _jsxs(Tooltip, {
                                        title: __("This vendor is no longer available and/or has been removed from the list of available vendors by the GVL. For this vendor, you can no longer request a consent from your visitors."),
                                        children: [
                                            /*#__PURE__*/ _jsxs("span", {
                                                style: {
                                                    opacity: 0.5
                                                },
                                                children: [
                                                    "#",
                                                    configuration.vendorId
                                                ]
                                            }),
                                            " ",
                                            /*#__PURE__*/ _jsx(Tag, {
                                                color: "error",
                                                children: __("Abandoned")
                                            })
                                        ]
                                    }),
                                    view === "vendors" && configuration === true && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Tag, {
                                                children: __("Already created")
                                            }),
                                            _.blocker !== false && /*#__PURE__*/ _jsx(Tag, {
                                                children: __("Content Blocker")
                                            })
                                        ]
                                    })
                                ]
                            });
                        }
                    }, "vendor"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Used in ad networks"),
                        dataIndex: "adNetworks",
                        render: (value, param)=>{
                            let { vendor } = param;
                            if (!vendor) {
                                return null;
                            }
                            const { id } = vendor;
                            const networks = adNetworksWithAllVendorIds.map((param)=>{
                                let { network, vendorIds } = param;
                                return vendorIds.indexOf(id) > -1 ? network : undefined;
                            }).filter(Boolean);
                            return networks.map((param)=>{
                                let { name, logo } = param;
                                return /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        /*#__PURE__*/ _jsx("img", {
                                            src: logo,
                                            style: {
                                                height: "1em"
                                            }
                                        }),
                                        " ",
                                        name
                                    ]
                                }, name);
                            });
                        }
                    }, "adNetworks"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Privacy policy"),
                        dataIndex: "privacyPolicy",
                        render: (value, param)=>{
                            let { vendor } = param;
                            if (!(vendor == null ? void 0 : vendor.urls)) {
                                return null;
                            }
                            const { urls: [{ privacy }] } = vendor;
                            return privacy && /*#__PURE__*/ _jsx("a", {
                                href: privacy,
                                target: "_blank",
                                rel: "noreferrer",
                                children: new URL(privacy).origin
                            });
                        }
                    }, "privacyPolicy"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Actions"),
                        dataIndex: "actions",
                        render: (value, record)=>{
                            const { configuration, vendor } = record;
                            return jsxJoin([
                                view === "vendor-configurations" && typeof configuration !== "boolean" && record.languages && /*#__PURE__*/ _jsx(ListMultilingualLanguages, {
                                    recordId: record.configuration.id,
                                    languages: record.languages,
                                    onClick: record.languageOnClick,
                                    wrapperProps: {
                                        wrapperClassName: "alignleft"
                                    }
                                }, "multilingual"),
                                view === "vendors" && configuration !== true && /*#__PURE__*/ _jsx("a", {
                                    onClick: ()=>store.onCreate(vendor),
                                    children: __("Create")
                                }, "create"),
                                view === "vendor-configurations" && typeof configuration !== "boolean" && vendor && /*#__PURE__*/ _jsx("a", {
                                    onClick: ()=>store.onCreateOrEditContentBlocker(configuration, vendor),
                                    children: configuration.blocker ? __("Edit Content Blocker") : __("Create Content Blocker")
                                }, "contentBlocker"),
                                view === "vendor-configurations" && typeof configuration !== "boolean" && vendor && /*#__PURE__*/ _jsx("a", {
                                    onClick: ()=>store.onEdit(configuration, vendor),
                                    children: __("Edit")
                                }, "edit"),
                                view === "vendor-configurations" && typeof configuration !== "boolean" && /*#__PURE__*/ _jsx(Popconfirm, {
                                    title: __("Are you sure you want to delete this vendor?"),
                                    placement: "bottomRight",
                                    onConfirm: ()=>store.onDelete(configuration),
                                    okText: __("Delete"),
                                    cancelText: __("Cancel"),
                                    overlayStyle: {
                                        maxWidth: 350
                                    },
                                    children: /*#__PURE__*/ _jsx("a", {
                                        children: __("Delete")
                                    })
                                }, "delete")
                            ], /*#__PURE__*/ _jsx(Divider, {
                                type: "vertical"
                            }));
                        }
                    }, "actions")
                ]
            })
        ]
    }) : busy ? /*#__PURE__*/ _jsx(Skeleton, {
        loading: true,
        active: true,
        paragraph: {
            rows: 1
        }
    }) : /*#__PURE__*/ _jsx(Empty, {
        description: __("You have not yet created a TCF vendor configuration."),
        children: /*#__PURE__*/ _jsx("a", {
            className: "button button-primary",
            onClick: store.onSwitchToVendorView,
            children: addText
        })
    });
};
export { ListTcfVendor };
